import { keycloak } from '@shared/config/KeycloakConfig';
import axios, { AxiosError } from 'axios';
import { VITE_APP_CLOUD_PRINTER_API_URL, VITE_APP_SORT_CENTER_API_URL } from '@shared/config/envConfig.ts';
import { ErrorDTO } from '@shared/types/api';

export const sort_center_api = axios.create({
  baseURL: VITE_APP_SORT_CENTER_API_URL,
});

export const cloudPrinterApi = axios.create({
  baseURL: VITE_APP_CLOUD_PRINTER_API_URL,
});

cloudPrinterApi.interceptors.request.use(
  async (config) => {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    config.headers['Time-Zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  },
  (error) => Promise.reject(error),
);

sort_center_api.interceptors.request.use(
  async (config) => {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
    // Content-Type не навязываем загрузкам файлов: axios при `application/json` сворачивает FormData
    // в JSON (defaults/transformRequest), файл теряется, и шлюз отвечает 415 на multipart-эндпоинт.
    // Для FormData заголовок оставляем пустым — браузер подставит multipart/form-data с boundary.
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = `application/json`;
    }
    config.headers['Time-Zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);



/**
 * Тело ошибки шлюза из ответа 422 ({ code, message }). Для ответов, пришедших Blob'ом,
 * вернёт undefined — такие эндпоинты (печать этикеток) кодами ошибок не различаются.
 */
export function getApiError(err: unknown): ErrorDTO | undefined {
  if (!axios.isAxiosError(err)) return undefined;

  const data = (err as AxiosError<ErrorDTO>).response?.data;
  if (!data || typeof data !== 'object' || data instanceof Blob) return undefined;

  return data;
}

export async function getErrorMessageAsync(
  err: unknown,
  fallback: string,
  codeMessageMap?: Record<string, string>,
): Promise<string> {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error ? err.message : fallback;
  }

  const e = err as AxiosError<ErrorDTO>;
  const data = e.response?.data;

  const mapByCode = (dto?: Partial<ErrorDTO> | null) => {
    const code = dto?.code;
    if (!code || !codeMessageMap) return undefined;
    return codeMessageMap[code];
  };

  // Если ответ пришёл Blob'ом 
  if (data instanceof Blob) {
    const text = await data.text();

    // пробуем JSON
    try {
      const json = JSON.parse(text) as Partial<ErrorDTO>;
      return mapByCode(json) ?? json.message ?? fallback;
    } catch {
      // если это не JSON, вернём как текст
      return text || e.message || fallback;
    }
  }

  // обычный JSON-ответ
  if (data && typeof data === 'object') {
    const dto = data as ErrorDTO;
    return mapByCode(dto) ?? dto.message ?? e.message ?? fallback;
  }

  return e.message ?? fallback;
}
